import { FC, useEffect, useMemo, useState } from 'react';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import useSiniestrosFilters from '../../hooks/useSiniestrosFilters';
import { FiltersState, SelectOption } from './types';
import { SiniestroStatus } from '../../types/siniestros.types';
import { ACCIDENT_TYPE_OPTIONS, buildComplaintYearOptions, INVESTIGATION_OPTIONS, LUGAR_OCURRENCIA_OPTIONS, SINIESTRO_STATUS_OPTIONS, SiniestroStatusEnum, SRT_STATUS_OPTIONS } from './constants';
import { useGetCentralOrganismsQuery, useGetOrganismsQuery } from '../../../../services/organismsApi';
import { useGetEmpresasQuery } from '../../../../services/companyApi';
import { useRoles } from '../../../../hooks/useRoles';
import useAsignacionSupervisor from '../../../../hooks/useAsignacionSupervisor';

interface SiniestrosFiltersProps {
    extraFilters: FiltersState;
    handleChangeExtraFilters: (e: any) => void;
    isShowMoreFiltersOpen: boolean;
    activeTab: SiniestroStatus;
    setActiveTab: (value: SiniestroStatus) => void;
    // Gestión de Archivos SRT aplica el cliente al momento, como primer filtro, y acota los
    // estados SRT (SMP-561)
    onClientChange?: (id: number | null) => void;
    srtStatusOptions?: SelectOption[];
    // ...con el rótulo de las otras tabs de esa pantalla
    srtStatusLabel?: string;
    // ...y no filtra por estado del siniestro ni por preventor (SMP-561)
    ocultarEstadoYPreventor?: boolean;
    // Solo Siniestros ROAM filtra por lugar de ocurrencia, y solo para BackOffice (SMP-585)
    conFiltroLugarOcurrencia?: boolean;
}

const SiniestrosFilters: FC<SiniestrosFiltersProps> = ({
    extraFilters,
    handleChangeExtraFilters,
    isShowMoreFiltersOpen,
    activeTab,
    setActiveTab,
    onClientChange,
    srtStatusOptions = SRT_STATUS_OPTIONS,
    srtStatusLabel,
    ocultarEstadoYPreventor = false,
    conFiltroLugarOcurrencia = false
}) => {
    const { t } = useTranslation('siniestrosRoam');
    const { isAdminBO, isSupervisorSIMP } = useRoles();

    const { clients, contracts, establishments, provinces, preventors, isLoading } =
        useSiniestrosFilters({ omitirPreventores: isSupervisorSIMP });
    const asignacion = useAsignacionSupervisor();

    // Re-fetches when clientId changes — same pattern as FiltersComponent
    const { data: centralOrganismsData, isFetching: isFetchingContracts } = useGetCentralOrganismsQuery({
        idOrganizacion: extraFilters?.clientId || undefined
    });

    // Sin default en la desestructuración: un [] nuevo por render re-disparaba el useEffect
    // de organismos sin fin mientras la query cargaba.
    const { data: organismsResponse, isFetching: isFetchingOrganisms } = useGetOrganismsQuery({
        idOrganizacion: extraFilters?.clientId || undefined,
        skip: 0,
        take: 999
    });
    const organismsData = organismsResponse?.data?.data;

    // El contrato se identifica por id de organismo central y no por nro de contrato:
    // varios organismos comparten el mismo número y el filtro mostraba y traía otro (SMP-482).
    const selectedOrganismoCentralId = extraFilters?.idOrganismoCentral;
    const [allContracts, setAllContracts] = useState<any[]>([]);
    const [allOrganisms, setAllOrganisms] = useState<any[]>([]);
    const [filteredOrganisms, setFilteredOrganisms] = useState<any[]>([]);
    const [adminEstablishments, setAdminEstablishments] = useState<any[]>([]);

    const { data: establishmentsData, isFetching: isFetchingEstablishments } = useGetEmpresasQuery(
        {
            skip: 0,
            take: 5000,
            idOrganizacion: extraFilters?.clientId || undefined,
            idOrganismoCentral: selectedOrganismoCentralId || undefined,
            idOrganismo: extraFilters?.organismoId || undefined
        },
        { skip: !isAdminBO }
    );

    useEffect(() => {
        const raw = centralOrganismsData?.data?.organismosCentral;
        if (raw) {
            setAllContracts(
                raw.map((c: any) => ({
                    id: c.id,
                    nombre: c.nombre || String(c.id)
                }))
            );
        } else {
            setAllContracts([]);
        }
    }, [centralOrganismsData]);

    useEffect(() => {
        if (organismsData && (organismsData as any[]).length > 0) {
            setAllOrganisms(
                (organismsData as any[]).map((o: any) => ({
                    label: o.razonSocial || o.nombre || String(o.id),
                    value: o.id,
                    idOrganismoCentral: o.idOrganismoCentral
                }))
            );
        } else {
            setAllOrganisms([]);
        }
    }, [organismsData]);

    // Cascade: contract changes → filter organisms
    useEffect(() => {
        if (selectedOrganismoCentralId) {
            setFilteredOrganisms(
                allOrganisms.filter((o) => o.idOrganismoCentral === selectedOrganismoCentralId)
            );
        } else {
            setFilteredOrganisms(allOrganisms);
        }
    }, [selectedOrganismoCentralId, allOrganisms]);

    useEffect(() => {
        if (establishmentsData?.data?.data) {
            setAdminEstablishments(
                (establishmentsData.data.data as any[]).map((e: any) => ({
                    label: e.nombre,
                    extra: { establishmentName: e.nombre }
                }))
            );
        } else {
            setAdminEstablishments([]);
        }
    }, [establishmentsData]);

    // Supervisor: Cliente, Contrato y Organismo solo ofrecen lo asignado, y si es uno solo va
    // precargado y grisado. Se vuelve a precargar después de "Limpiar" (SMP-636)
    const organismOptionsSupervisor = useMemo(
        () =>
            asignacion.organismos
                .filter(
                    (o) =>
                        !selectedOrganismoCentralId ||
                        o.idOrganismoCentral === selectedOrganismoCentralId
                )
                .map((o) => ({ label: o.nombre, value: o.id })),
        [asignacion.organismos, selectedOrganismoCentralId]
    );

    useEffect(() => {
        if (!isSupervisorSIMP) return;
        const precargas: Array<[keyof FiltersState, number | undefined]> = [
            ['clientId', asignacion.cliente?.id],
            ['idOrganismoCentral', asignacion.idContratoUnico],
            ['organismoId', asignacion.idOrganismoUnico]
        ];
        precargas.forEach(([name, value]) => {
            if (value && extraFilters?.[name] !== value) {
                handleChangeExtraFilters({ target: { name, value } });
            }
        });
    }, [
        isSupervisorSIMP,
        asignacion.cliente?.id,
        asignacion.idContratoUnico,
        asignacion.idOrganismoUnico,
        extraFilters,
        handleChangeExtraFilters
    ]);

    const complaintYearOptions = buildComplaintYearOptions();

    const statusValueByTab: number | undefined =
        activeTab === 'PENDING' ? SiniestroStatusEnum.PENDING :
        activeTab === 'OPEN' ? SiniestroStatusEnum.OPEN :
        activeTab === 'FINALIZED' ? SiniestroStatusEnum.FINISHED :
        activeTab === 'CANCELLED' ? SiniestroStatusEnum.CANCELLED :
        undefined;

    useEffect(() => {
        if (activeTab !== 'PENDING' || !extraFilters?.idPreventor) return;
        handleChangeExtraFilters({ target: { name: 'idPreventor', value: null } });
    }, [activeTab, extraFilters?.idPreventor, handleChangeExtraFilters]);

    const handleStatusChange = (value: any) => {
        const nextStatus = value?.id;
        if (nextStatus === SiniestroStatusEnum.OPEN) setActiveTab('OPEN');
        if (nextStatus === SiniestroStatusEnum.PENDING) setActiveTab('PENDING');
        if (nextStatus === SiniestroStatusEnum.FINISHED) setActiveTab('FINALIZED');
        if (nextStatus === SiniestroStatusEnum.CANCELLED) setActiveTab('CANCELLED');
    };

    // Cascade helpers
    const clearFrom = (from: 'client' | 'contract' | 'organism') => {
        if (from === 'client') {
            handleChangeExtraFilters({ target: { name: 'idOrganismoCentral', value: null } });
        }
        if (from === 'client' || from === 'contract') {
            handleChangeExtraFilters({ target: { name: 'organismoId', value: null } });
        }
        handleChangeExtraFilters({ target: { name: 'establishmentName', value: null } });
    };

    const handleClientChange = (value: any) => {
        if (onClientChange) {
            onClientChange(value?.id || null);
            return;
        }
        handleChangeExtraFilters({ target: { name: 'clientId', value: value?.id || null } });
        if (isAdminBO) clearFrom('client');
    };

    const clientFilter = (
        <Grid item xs={12} md={3}>
            <AutocompleteComponent
                name="clientId"
                label={t('filters.client')}
                options={clients}
                value={clients.find((c) => c.id === extraFilters?.clientId) || null}
                loading={isLoading}
                onChange={handleClientChange}
                templateLabel={(o: any) => o.label}
                clearable
            />
        </Grid>
    );

    const nroRoamFilter = (
        <Grid item xs={12} md={3}>
            <TextField
                fullWidth
                label={t('filters.nroRoam')}
                value={extraFilters?.nroRoam || ''}
                onChange={(e) =>
                    handleChangeExtraFilters({
                        target: { name: 'nroRoam', value: e.target.value }
                    })
                }
            />
        </Grid>
    );

    const employeeFilter = (
        <Grid item xs={12} md={3}>
            <TextField
                fullWidth
                label={t('filters.employee')}
                value={extraFilters?.employee || ''}
                onChange={(e) =>
                    handleChangeExtraFilters({
                        target: { name: 'employee', value: e.target.value }
                    })
                }
            />
        </Grid>
    );

    const establishmentFilter = (
        <Grid item xs={12} md={3}>
            <AutocompleteComponent
                name="establishmentName"
                label={t('filters.establishment')}
                options={establishments}
                value={
                    establishments.find(
                        (e) => e.extra?.establishmentName === extraFilters?.establishmentName
                    ) || null
                }
                loading={isLoading}
                onChange={(value: any) =>
                    handleChangeExtraFilters({
                        target: {
                            name: 'establishmentName',
                            value: value?.extra?.establishmentName || null
                        }
                    })
                }
                templateLabel={(o: any) => o.label}
                clearable
            />
        </Grid>
    );

    const provinceFilter = (
        <Grid item xs={12} md={3}>
            <AutocompleteComponent
                name="provinceId"
                label={t('filters.province')}
                options={provinces}
                value={provinces.find((p) => p.id === extraFilters?.provinceId) || null}
                loading={isLoading}
                onChange={(value: any) =>
                    handleChangeExtraFilters({
                        target: { name: 'provinceId', value: value?.id || null }
                    })
                }
                templateLabel={(o: any) => o.label}
                clearable
            />
        </Grid>
    );

    const accidentTypeFilter = (
        <Grid item xs={12} md={3}>
            <AutocompleteComponent
                name="accidentType"
                label={t('filters.accidentType')}
                options={ACCIDENT_TYPE_OPTIONS}
                value={
                    ACCIDENT_TYPE_OPTIONS.find(
                        (o) => o.id === (extraFilters?.accidentType ?? 0)
                    ) || ACCIDENT_TYPE_OPTIONS[0]
                }
                onChange={(value: any) =>
                    handleChangeExtraFilters({
                        target: {
                            name: 'accidentType',
                            value: value?.id === 0 ? undefined : value?.id
                        }
                    })
                }
                templateLabel={(o: any) => o.label}
                clearable={false}
            />
        </Grid>
    );

    return (
        <Grid container spacing={2}>
            {isAdminBO ? (
                <>
                    {/* AdminBO row 1: Cliente, Contrato, Organismo, Establecimiento */}

                    {clientFilter}

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="idOrganismoCentral"
                            label={t('filters.contract')}
                            options={allContracts}
                            value={
                                allContracts.find((c) => c.id === selectedOrganismoCentralId) ||
                                null
                            }
                            loading={isFetchingContracts}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idOrganismoCentral', value: value?.id || null }
                                });
                                clearFrom('contract');
                            }}
                            templateLabel={(o: any) => o.nombre || ''}
                            clearable
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="organismoId"
                            label={t('filters.organism')}
                            options={filteredOrganisms}
                            value={
                                filteredOrganisms.find(
                                    (o) => o.value === extraFilters?.organismoId
                                ) || null
                            }
                            loading={isFetchingOrganisms}
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'organismoId', value: value?.value || null }
                                });
                                clearFrom('organism');
                            }}
                            templateLabel={(o: any) => o.label}
                            clearable
                        />
                    </Grid>

                    {/* AdminBO row 2: Establecimiento (always visible, filtered by cascade) */}
                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="establishmentName"
                            label={t('filters.establishment')}
                            options={adminEstablishments}
                            value={
                                adminEstablishments.find(
                                    (e) =>
                                        e.extra?.establishmentName ===
                                        extraFilters?.establishmentName
                                ) || null
                            }
                            loading={isFetchingEstablishments}
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'establishmentName',
                                        value: value?.extra?.establishmentName || null
                                    }
                                })
                            }
                            templateLabel={(o: any) => o.label}
                            clearable
                        />
                    </Grid>
                    {nroRoamFilter}
                </>
            ) : isSupervisorSIMP ? (
                <>
                    {/* Supervisor: fila 1 Cliente, Contrato, Organismo y Establecimiento; fila 2
                        Nro ROAM, Empleado, Provincia y Tipo de siniestro; el resto en
                        "Más filtros" (SMP-636) */}
                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="clientId"
                            label={t('filters.client')}
                            options={asignacion.cliente ? [asignacion.cliente] : []}
                            value={asignacion.cliente}
                            onChange={() => undefined}
                            templateLabel={(o: any) => o.nombre}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="idOrganismoCentral"
                            label={t('filters.contract')}
                            options={asignacion.contratos}
                            value={
                                asignacion.contratos.find(
                                    (c) => c.id === selectedOrganismoCentralId
                                ) || null
                            }
                            onChange={(value: any) => {
                                handleChangeExtraFilters({
                                    target: { name: 'idOrganismoCentral', value: value?.id || null }
                                });
                                handleChangeExtraFilters({
                                    target: { name: 'organismoId', value: null }
                                });
                            }}
                            templateLabel={(o: any) => o.nombre}
                            disabled={asignacion.contratos.length <= 1}
                            clearable={asignacion.contratos.length > 1}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="organismoId"
                            label={t('filters.organism')}
                            options={organismOptionsSupervisor}
                            value={
                                organismOptionsSupervisor.find(
                                    (o) => o.value === extraFilters?.organismoId
                                ) || null
                            }
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: { name: 'organismoId', value: value?.value || null }
                                })
                            }
                            templateLabel={(o: any) => o.label}
                            disabled={asignacion.organismos.length <= 1}
                            clearable={asignacion.organismos.length > 1}
                        />
                    </Grid>

                    {establishmentFilter}
                    {nroRoamFilter}
                    {employeeFilter}
                    {provinceFilter}
                    {accidentTypeFilter}
                </>
            ) : (
                <>
                    {/* Non-AdminBO row 1: original layout */}
                    {onClientChange && clientFilter}

                    {nroRoamFilter}

                    {!onClientChange && clientFilter}

                    {employeeFilter}

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="idOrganismoCentral"
                            label={t('filters.contract')}
                            options={contracts}
                            value={
                                contracts.find((c) => c.id === selectedOrganismoCentralId) || null
                            }
                            loading={isLoading}
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: { name: 'idOrganismoCentral', value: value?.id || null }
                                })
                            }
                            templateLabel={(o: any) => o.label}
                            clearable
                        />
                    </Grid>
                </>
            )}

            {/* Fuera de "Más filtros": es el filtro que dice a qué siniestros se les puede
                cargar una visita (SMP-585). Solo el administrador BO: la aseguradora viewer no
                carga visitas (SMP-628) */}
            {conFiltroLugarOcurrencia && isAdminBO && (
                <Grid item xs={12} md={3}>
                    <AutocompleteComponent
                        name="lugarOcurrencia"
                        label={t('filters.occurrenceLocation')}
                        options={LUGAR_OCURRENCIA_OPTIONS}
                        value={
                            LUGAR_OCURRENCIA_OPTIONS.find(
                                (o) => o.extra?.value === extraFilters?.lugarOcurrencia
                            ) || LUGAR_OCURRENCIA_OPTIONS[0]
                        }
                        onChange={(value: any) =>
                            handleChangeExtraFilters({
                                target: {
                                    name: 'lugarOcurrencia',
                                    value: value?.extra?.value ?? null
                                }
                            })
                        }
                        templateLabel={(o: any) => o.label}
                        clearable={false}
                    />
                </Grid>
            )}

            {isShowMoreFiltersOpen && (
                <>
                    {isAdminBO && employeeFilter}

                    {!isAdminBO && !isSupervisorSIMP && establishmentFilter}

                    {!isSupervisorSIMP && provinceFilter}

                    {!isSupervisorSIMP && accidentTypeFilter}

                    {!ocultarEstadoYPreventor && (
                        <Grid item xs={12} md={3}>
                            {/* El supervisor solo ve siniestros Finalizados: el estado queda fijo (SMP-460) */}
                            {isSupervisorSIMP ? (
                                <TextField
                                    fullWidth
                                    label={t('filters.status')}
                                    value="Finalizado"
                                    disabled
                                />
                            ) : (
                                <AutocompleteComponent
                                    name="status"
                                    label={t('filters.status')}
                                    options={SINIESTRO_STATUS_OPTIONS}
                                    value={
                                        SINIESTRO_STATUS_OPTIONS.find(
                                            (o) => o.id === statusValueByTab
                                        ) || (activeTab === 'ALL' ? null : SINIESTRO_STATUS_OPTIONS[0])
                                    }
                                    onChange={handleStatusChange}
                                    templateLabel={(o: any) => o.label}
                                    clearable={false}
                                />
                            )}
                        </Grid>
                    )}

                    {activeTab !== 'PENDING' && !isSupervisorSIMP && !ocultarEstadoYPreventor && (
                        <Grid item xs={12} md={3}>
                            <AutocompleteComponent
                                name="idPreventor"
                                label={t('filters.preventor')}
                                options={preventors}
                                value={
                                    preventors.find(
                                        (p) => p.id === extraFilters?.idPreventor
                                    ) || null
                                }
                                onChange={(value: any) =>
                                    handleChangeExtraFilters({
                                        target: {
                                            name: 'idPreventor',
                                            value: value?.id || null
                                        }
                                    })
                                }
                                templateLabel={(o: any) => o.label}
                                clearable
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="srtStatus"
                            label={srtStatusLabel ?? t('filters.srtStatus')}
                            options={srtStatusOptions}
                            value={
                                srtStatusOptions.find(
                                    (o) => o.id === (extraFilters?.srtStatus ?? 0)
                                ) || srtStatusOptions[0]
                            }
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'srtStatus',
                                        value: value?.id === 0 ? undefined : value?.id
                                    }
                                })
                            }
                            templateLabel={(o: any) => o.label}
                            clearable={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <AutocompleteComponent
                            name="investigation"
                            label={t('filters.investigation')}
                            options={INVESTIGATION_OPTIONS}
                            value={
                                extraFilters?.investigation === true
                                    ? INVESTIGATION_OPTIONS[1]
                                    : extraFilters?.investigation === false
                                      ? INVESTIGATION_OPTIONS[2]
                                      : INVESTIGATION_OPTIONS[0]
                            }
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'investigation',
                                        value: value?.id === 0 ? null : value?.extra?.value
                                    }
                                })
                            }
                            templateLabel={(o: any) => o.label}
                            clearable={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{t('filters.year')}</InputLabel>
                            <Select
                                label={t('filters.year')}
                                value={extraFilters?.anioSiniestro ?? 0}
                                onChange={(e) =>
                                    handleChangeExtraFilters({
                                        target: {
                                            name: 'anioSiniestro',
                                            value: e.target.value === 0 ? undefined : e.target.value
                                        }
                                    })
                                }
                            >
                                {complaintYearOptions.map((o) => (
                                    <MenuItem key={o.id} value={o.id}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default SiniestrosFilters;
